import { useEffect, useMemo, useState } from "react";
import { Filter, X } from "lucide-react";
import { useAnime } from "../context/AnimeContext";
import AnimeCard from "./AnimeCard";

/**
 * GenreFilter (Minimal + Sleek, no dropdown)
 * - Always visible, compact, modern.
 * - Uses a "single-line chips rail" that scrolls horizontally on mobile (no big wrap).
 * - Selected chips stay in-view; clear button is subtle.
 * - Results grid is mobile-first (2 cols) and scales up smoothly.
 */
export default function GenreFilter() {
  const { animeList } = useAnime();

  const [selectedGenres, setSelectedGenres] = useState([]);
  const [filteredAnime, setFilteredAnime] = useState([]);

  // Extract unique genres from all anime (memoized)
  const allGenres = useMemo(() => {
    const list = Array.isArray(animeList) ? animeList : [];
    const set = new Set();

    list.forEach((anime) => {
      if (anime?.Genres && Array.isArray(anime.Genres)) {
        anime.Genres.forEach((g) => g && set.add(g));
      }
    });

    return [...set].sort((a, b) => String(a).localeCompare(String(b)));
  }, [animeList]);

  // Filter anime based on selected genres
  useEffect(() => {
    const list = Array.isArray(animeList) ? animeList : [];

    if (selectedGenres.length === 0) {
      setFilteredAnime([]);
      return;
    }

    const filtered = list.filter((anime) => {
      if (!anime?.Genres || !Array.isArray(anime.Genres)) return false;
      return selectedGenres.some((genre) => anime.Genres.includes(genre));
    });

    setFilteredAnime(filtered);
  }, [selectedGenres, animeList]);

  const toggleGenre = (genre) => {
    setSelectedGenres((prev) =>
      prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre]
    );
  };

  const clearFilters = () => setSelectedGenres([]);

  if (allGenres.length === 0) return null;

  /* -----------------------------
     Styles (minimal, modern)
  ------------------------------ */

  const PANEL =
    "w-full rounded-[24px] border border-white/10 bg-white/[0.03] backdrop-blur-xl " +
    "shadow-[0_22px_90px_rgba(0,0,0,0.45)]";

  // Tighter, calmer header
  const HEADER =
    "flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-b border-white/10";

  const TITLE =
    "text-[13px] sm:text-[14px] font-semibold text-white/90 tracking-tight";

  const SUB =
    "text-[10px] uppercase tracking-[0.22em] text-white/45 mt-0.5";

  // Horizontal chips rail (mobile scroll), wraps only on larger screens
  const RAIL_WRAP = "px-4 sm:px-5 py-3";
  const RAIL =
    "flex items-center gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1 " +
    "sm:flex-wrap sm:overflow-visible sm:-mx-0 sm:px-0";

  const CHIP = (active) =>
    [
      "shrink-0",
      "h-9 px-3.5 rounded-full border",
      "text-[12px] font-semibold",
      "transition-colors",
      "focus:outline-none focus:ring-2 focus:ring-emerald-300/25",
      active
        ? "border-emerald-400/45 bg-emerald-500/12 text-emerald-100"
        : "border-white/10 bg-black/20 text-white/70 hover:bg-white/[0.04] hover:border-white/20",
    ].join(" ");

  const CLEAR =
    "inline-flex items-center gap-2 h-9 px-3 rounded-full border " +
    "border-white/10 bg-black/20 text-white/60 text-[12px] font-semibold " +
    "hover:bg-white/[0.04] hover:border-white/20 transition";

  const META_ROW = "px-4 sm:px-5 pb-3";
  const META =
    "flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3";

  const COUNTER =
    "text-[12px] text-white/55 leading-relaxed";

  const RESULTS_WRAP = "px-4 sm:px-5 pb-5";

  const GRID =
    "mt-3 grid gap-4 sm:gap-5 " +
    "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5";

  const CARD_WRAP =
    "rounded-2xl overflow-hidden border border-white/10 bg-black/20";

  return (
    <section className={PANEL}>
      {/* Header */}
      <div className={HEADER}>
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-9 w-9 rounded-2xl grid place-items-center bg-black/25 border border-white/10">
            <Filter className="h-4 w-4 text-emerald-200/90" />
          </div>

          <div className="min-w-0">
            <div className={TITLE}>Browse by Genre</div>
            <div className={SUB}>filter discovery</div>
          </div>
        </div>

        {/* Minimal clear */}
        {selectedGenres.length > 0 ? (
          <button onClick={clearFilters} className={CLEAR} type="button">
            <X size={16} className="opacity-75" />
            Clear
          </button>
        ) : (
          <div className="text-[11px] text-white/35 hidden sm:block">
            Tap genres to filter
          </div>
        )}
      </div>

      {/* Chips rail (minimal footprint, scrolls on mobile) */}
      <div className={RAIL_WRAP}>
        <div className={RAIL} aria-label="Genre filters">
          {allGenres.map((genre) => {
            const active = selectedGenres.includes(genre);
            return (
              <button
                key={genre}
                onClick={() => toggleGenre(genre)}
                className={CHIP(active)}
                type="button"
              >
                {genre}
              </button>
            );
          })}
        </div>
      </div>

      {/* Meta row (compact) */}
      <div className={META_ROW}>
        <div className={META}>
          {selectedGenres.length > 0 ? (
            <div className={COUNTER}>
              Found{" "}
              <span className="text-white/85 font-semibold">
                {filteredAnime.length}
              </span>{" "}
              for{" "}
              <span className="text-emerald-200 font-semibold">
                {selectedGenres.join(", ")}
              </span>
            </div>
          ) : (
            <div className={COUNTER}>
              Select one or more genres to discover anime.
            </div>
          )}

          {/* Optional subtle hint on mobile */}
          <div className="sm:ml-auto text-[11px] text-white/35">
            <span className="sm:hidden">Swipe genres →</span>
          </div>
        </div>
      </div>

      {/* Results (only when filtering) */}
      {selectedGenres.length > 0 ? (
        <div className={RESULTS_WRAP}>
          {filteredAnime.length > 0 ? (
            <>
              <div className={GRID}>
                {filteredAnime.slice(0, 20).map((anime) => (
                  <div key={anime.AnimeID} className={CARD_WRAP}>
                    <AnimeCard anime={anime} variant="genre"/>
                  </div>
                ))}
              </div>

              {filteredAnime.length > 20 && (
                <div className="mt-5 text-center text-[11px] text-white/50">
                  Showing 20 of {filteredAnime.length}.{" "}
                  <span className="text-emerald-200">
                    Use search to view all.
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="mt-3 rounded-2xl border border-white/10 bg-black/18 p-6 text-white/70 text-center">
              No anime found with the selected genres.
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}
